import { create } from 'zustand'
import { useAccountMatterStore } from './use-account-matter-store';
import { ONEHUNDRED_MEGABYTES, sleep } from '../utils';
import { GetFileFailure, isGetFileFailure, MatterFileInfo } from '@/app/api/matter/files/types'
import { useModalStore } from './use-modal-store';
import { KLSource } from 'bridging';

const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/gi;
const isGuid = (str: string) => !!str.match(guidRegex);
type MatterFilesStore = {
  loading: boolean;
  errorMessage: string;
  matterFiles: MatterFileInfo[];
  klSources: KLSource[];

  setFileIdsToFetch: (fileIdsToFetch: string[]) => void;
  setKLFileIdsToFetch: (fileIdsToFetch: string[]) => void;
  // I don't think these two belong here they are just used to hold state for something else
  initialFileIds: string[] | undefined;
  setInitialFileIds: (initialFileIds: MatterFilesStore["initialFileIds"]) => void;
  // Function to clear all file IDs when starting a new question
  clearAllFileIds: () => void;
}

export const useMatterFilesStore = create<MatterFilesStore>((set, get) => {
  let allFileIds: string[] = [];
  let allKlFileIds: string[] = [];

  return {
    loading: true,
    errorMessage: '',
    matterFiles: [],
    klSources: [],

    setFileIdsToFetch: (fileIdsToFetch) => {
      const newFileIds = fileIdsToFetch.filter(isGuid).filter(id => !allFileIds.includes(id));
      allFileIds = [...allFileIds, ...newFileIds];
      if (newFileIds.length) {
        const matterFilesIds = get().matterFiles.map((f) => f.id);
        // this could be different from unique above because it would include failures I guess?
        const missingFileIds = allFileIds.filter((fileId) => !matterFilesIds.includes(fileId));
        fetchFilesAndUpdateState({ missingFileIds })
      }
    },
    setKLFileIdsToFetch: (fileIdsToFetch) => {
      const newFileIds = fileIdsToFetch.filter(isGuid).filter(id => !allKlFileIds.includes(id));
      allKlFileIds = [...allKlFileIds, ...newFileIds];
      if (newFileIds.length) {
        const klFilesIds = get().klSources.map((f) => f.id);
        const missingFileIds = allKlFileIds.filter((fileId) => !klFilesIds.includes(fileId));
        fetchKLFilesAndUpdateState({ missingFileIds })
      }
    },
    // I don't think these two belong here they are just used to hold state for something else
    initialFileIds: undefined,
    setInitialFileIds: (initialFileIds) => set({ initialFileIds }),
    // Function to clear accumulated file IDs when starting a new question
    clearAllFileIds: () => {
      allFileIds = [];
      allKlFileIds = [];
      set({ matterFiles: [], klSources: [] });
    }
  }
});

const fetchFilesAndUpdateState = async ({ missingFileIds, disableLoadingIndicator }: { missingFileIds: string[], disableLoadingIndicator?: boolean }) => {
  const { matterId, accountId } = useAccountMatterStore.getState();

  if (!matterId || !accountId || !missingFileIds.length) {
    useMatterFilesStore.setState({ loading: false, errorMessage: '' });
    return;
  }

  if (!disableLoadingIndicator) {
    useMatterFilesStore.setState({ loading: true });
  }
  useMatterFilesStore.setState({ errorMessage: '' });

  try {
    const maxAttempts = 10
    let attempt = 0
    while (true) {
      const response = await fetch(
        `/api/matter/files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ matterId, accountId, fileIds: missingFileIds }),
        }
      )
      if (response.status === 401) {
        if (attempt >= maxAttempts) {
          throw new Error('Unauthorized from fusion')
        }
        console.warn('Unauthorized from fusion, sleeping and retrying')
        attempt += 1
        await sleep(3000)
        continue
      }
      if (!response.ok) {
        throw new Error('Something went wrong trying to get files')
      }

      const { filesOrFailures } = (await response.json()) as { filesOrFailures: (MatterFileInfo | GetFileFailure)[] }
      const { files, failures } = filesOrFailures.reduce((acc, d) => {
        if (isGetFileFailure(d)) {
          acc.failures.push(d)
        } else {
          acc.files.push(d)
        }
        return acc
      }, { files: [], failures: [] } as { files: MatterFileInfo[]; failures: GetFileFailure[] })

      if (files.some(file => (file.sizeBytes || 0) > ONEHUNDRED_MEGABYTES)) {
        useModalStore.getState().showFileWarningModal('Archie cannot answer questions on files of this size. Please choose another file to proceed.')
        return
      }

      const existingFiles = useMatterFilesStore.getState().matterFiles;
      const newFiles = files.filter((file) => !existingFiles.some((existingFile) => existingFile.id === file.id))
      useMatterFilesStore.setState({ matterFiles: [...existingFiles, ...newFiles] });
      break
    }
  } catch (err) {
    if (err instanceof Error) {
      useMatterFilesStore.setState({ errorMessage: err.message });
    }
  } finally {
    useMatterFilesStore.setState({ loading: false });
  }
}

const fetchKLFilesAndUpdateState = async ({ missingFileIds, disableLoadingIndicator }: { missingFileIds: string[], disableLoadingIndicator?: boolean }) => {
  const { accountId } = useAccountMatterStore.getState();
  if (!accountId || !missingFileIds.length) {
    useMatterFilesStore.setState({ loading: false, errorMessage: '' });
    return;
  }

  if (!disableLoadingIndicator) {
    useMatterFilesStore.setState({ loading: true });
  }
  useMatterFilesStore.setState({ errorMessage: '' });

  try {
    const maxAttempts = 10
    let attempt = 0
    while (true) {
      const response = await fetch(
        `/api/knowledge-library/files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ accountId, sourceIds: missingFileIds }),
        }
      )
      if (response.status === 401) {
        if (attempt >= maxAttempts) {
          throw new Error('Unauthorized from kl retrieval')
        }
        console.warn('Unauthorized from kl retrieval, sleeping and retrying')
        attempt += 1
        await sleep(3000)
        continue
      }
      if (!response.ok) {
        throw new Error('Something went wrong trying to get files')
      }

      const { filesOrFailures } = (await response.json()) as { filesOrFailures: (KLSource | GetFileFailure)[] }
      const { files, failures } = filesOrFailures.reduce((acc, d) => {
        if (isGetFileFailure(d)) {
          acc.failures.push(d)
        } else {
          acc.files.push(d)
        }
        return acc
      }, { files: [], failures: [] } as { files: KLSource[]; failures: GetFileFailure[] })

      const existingFiles = useMatterFilesStore.getState().klSources;
      const newFiles = files.filter((file) => !existingFiles.some((existingFile) => existingFile.id === file.id))
      useMatterFilesStore.setState({ klSources: [...existingFiles, ...newFiles] });
      break
    }
  } catch (err) {
    if (err instanceof Error) {
      useMatterFilesStore.setState({ errorMessage: err.message });
    }
  } finally {
    useMatterFilesStore.setState({ loading: false });
  }
}
